import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { useTranslation } from 'react-i18next';
import { ProductTemplateOption, ProductTemplateSelectedOption } from '@/shared/components/PresetSelection/types.ts';
import { StandaloneProductCard } from '@/shared/components/PresetSelection/components/StandaloneProductCard.tsx';
import { ProductWithTransportCard } from '@/shared/components/PresetSelection/components/ProductWithTransportCard.tsx';
import { PRICING_STRATEGY } from '@schuettflix/interfaces';

const STANDALONE_PRICING_STRATEGY: PRICING_STRATEGY = 'STANDARD';

type ProductTemplateListProps = {
    options: ProductTemplateOption[];
    isExpandable: boolean;
    onSelect: (option: ProductTemplateSelectedOption) => void;
};

export function ProductTemplateList({ options, isExpandable, onSelect }: ProductTemplateListProps) {
    const { t } = useTranslation();
    const materialIncludingTransport = options.filter(option => !option.isStandalone);

    const transportOnly = options.filter(
        option => option.isStandalone && getProductCategoryByProductType(option.productType) === 'TRANSPORT'
    );
    const materialOnly = options.filter(
        option => option.isStandalone && getProductCategoryByProductType(option.productType) === 'MATERIAL'
    );
    const serviceOnly = options.filter(option =>
        option.isStandalone
            ? ['TRANSPORT_SERVICE', 'MATERIAL_SERVICE'].includes(getProductCategoryByProductType(option.productType))
            : false
    );

    return (
        <div className="flex w-full flex-col gap-8 pb-6">
            {!!materialIncludingTransport.length && (
                <MaterialWithTransportSection
                    options={materialIncludingTransport}
                    isExpandable={isExpandable}
                    onSelect={onSelect}
                />
            )}
            {!!transportOnly.length && (
                <StandaloneSection
                    pricingStrategy={STANDALONE_PRICING_STRATEGY}
                    title={t('products.richCatalogue.transportHeader')}
                    options={transportOnly}
                    onSelect={onSelect}
                />
            )}
            {!!materialOnly.length && (
                <StandaloneSection
                    pricingStrategy={STANDALONE_PRICING_STRATEGY}
                    title={t('products.richCatalogue.materialHeader')}
                    options={materialOnly}
                    onSelect={onSelect}
                />
            )}
            {!!serviceOnly.length && (
                <StandaloneSection
                    pricingStrategy={STANDALONE_PRICING_STRATEGY}
                    title={t('products.richCatalogue.serviceHeader')}
                    options={serviceOnly}
                    onSelect={onSelect}
                />
            )}
        </div>
    );
}

type SectionProps = {
    options: ProductTemplateOption[];
    isExpandable: boolean;
    onSelect: (option: ProductTemplateSelectedOption) => void;
};

function MaterialWithTransportSection({ options, isExpandable, onSelect }: SectionProps) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col">
            <div className="pb-2">
                <h3 className="text font-copy-xs-strong uppercase">
                    {`${t('products.richCatalogue.materialHeader')} + ${t('products.richCatalogue.transportHeader')}`}
                </h3>
            </div>
            <div className="grid gap-2">
                {options.map((option, index) => (
                    <ProductWithTransportCard
                        key={index}
                        option={option}
                        isExpandable={isExpandable}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </div>
    );
}

function StandaloneSection({
    title,
    options,
    onSelect,
    pricingStrategy,
}: Omit<SectionProps, 'isExpandable'> & { title: string; pricingStrategy: PRICING_STRATEGY }) {
    return (
        <div className="flex flex-col">
            <div className="pb-2">
                <h3 className="text font-copy-xs-strong uppercase">{title}</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
                {options.map((option, index) => (
                    <StandaloneProductCard
                        key={index}
                        productType={option.productType}
                        pricingStrategy={pricingStrategy}
                        disposalPurpose={option.formPreset?.disposalPurpose}
                        unit={option.formPreset?.billingUnit}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </div>
    );
}
